import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
};
export const usePriorityTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const priorityTreeItems: Record<ID, TreeItem> = {
      [TaskPriorityOptions.High]: {
        id: TaskPriorityOptions.High,
        type: TreeItemTypes.Priority,
        name: t(`general.labels.priority.options.${TaskPriorityOptions.High}`),
        children: [],
        isFolder: true,
      },
      [TaskPriorityOptions.Medium]: {
        id: TaskPriorityOptions.Medium,
        type: TreeItemTypes.Priority,
        name: t(
          `general.labels.priority.options.${TaskPriorityOptions.Medium}`,
        ),
        children: [],
        isFolder: true,
      },
      [TaskPriorityOptions.Low]: {
        id: TaskPriorityOptions.Low,
        type: TreeItemTypes.Priority,
        name: t(`general.labels.priority.options.${TaskPriorityOptions.Low}`),
        children: [],
        isFolder: true,
      },
      [TaskPriorityOptions.None]: {
        id: TaskPriorityOptions.None,
        type: TreeItemTypes.Priority,
        name: t(`general.labels.priority.options.${TaskPriorityOptions.None}`),
        children: [],
        isFolder: true,
      },
    };
    const priorityIds = Object.keys(priorityTreeItems);

    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? priorityIds.reduce<ID[]>((acc, priority) => {
            const lifeAreaOrder = customOrder?.[priority] ?? [];
            acc.push(...lifeAreaOrder);
            return acc;
          }, [])
        : [];

    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      const priorityTreeItem =
        acc[task.priority!] ?? acc[TaskPriorityOptions.None];
      priorityTreeItem.children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
      return acc;
    }, priorityTreeItems);

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: priorityIds,
      },
      ...allTreeItems,
    };
  }, [customOrder, sortingMode, t, tasks, today, weekStartsOn]);
};
